import {
  Outlet,
  createRootRoute,
  useRouter,
  useRouterState
} from '@tanstack/react-router'
import { useEffect, useRef, useState, type JSX } from 'react'
import { Toaster } from 'sonner'

import { ThemeToggle } from '../components/theme-toggle.tsx'
import { Button } from '../design-system/button.tsx'
import { Icon } from '../design-system/icon.tsx'
import { Link } from '../design-system/link.tsx'
import { NavSheet } from '../design-system/nav-sheet.tsx'
import { Notice } from '../design-system/notice.tsx'
import {
  groupInternalPages,
  normalizePath,
  type InternalPage
} from '../lib/pages.ts'
import { INTERNAL_PAGES } from './-nav.ts'
import { cn } from '../lib/cn.ts'
import { IDLE_WARNING_EVENT } from '../lib/idle.ts'
import {
  readCollapsedGroups,
  writeCollapsedGroups
} from '../lib/nav-group-store.ts'
import { useSession } from '../session.tsx'

export const Route = createRootRoute({
  component: RootLayout,
  errorComponent: function RouterError({ error }) {
    return (
      <div className="p-8">
        <Notice tone="danger" data-testid="router-error">
          {error.message}
        </Notice>
      </div>
    )
  }
})

const SIDEBAR_CONTROL_CLASS =
  'text-sidebar-foreground hover:bg-sidebar-accent/40 hover:text-sidebar-foreground focus-visible:ring-sidebar-ring'

/**
 * The rail's sections, grouped once.
 *
 * `INTERNAL_PAGES` is frozen, so the grouping cannot change while the app
 * runs. `groupInternalPages` also returns the pages carrying no group; every
 * page here carries one and `-nav.test.ts` keeps it that way, so the rail
 * reads the sections alone. The framework keeps its flat branch for a rail
 * that may not be this one.
 */
const NAV_SECTIONS = groupInternalPages(INTERNAL_PAGES).sections

function rowClass(active: boolean): string {
  return cn(
    'text-sidebar-foreground/80 flex items-center gap-3 rounded-md px-3 py-2 text-sm',
    'hover:bg-sidebar-accent/40 focus-visible:ring-sidebar-ring cursor-pointer',
    'text-left focus-visible:ring-2 focus-visible:outline-none',
    active && 'bg-sidebar-accent/60 text-sidebar-accent-foreground font-medium'
  )
}

/** A group name as a `data-testid` fragment: lower-cased, spaces to hyphens. "CS Tools" becomes "cs-tools". */
function slugifyGroup(group: string): string {
  return group.trim().toLowerCase().replace(/\s+/g, '-')
}

/** Every page in this console renders the same row; a group header and a flat entry both build one from it. */
function NavRow({
  page,
  active,
  testId,
  onNavigate
}: {
  page: InternalPage
  active: boolean
  testId: string
  onNavigate: () => void
}): JSX.Element {
  return (
    <Link
      data-testid={testId}
      href={page.path}
      onNavigate={onNavigate}
      className={rowClass(active)}
      aria-current={active ? 'page' : undefined}
    >
      <Icon name="app-window" className="size-4 shrink-0" />
      {page.title}
    </Link>
  )
}

/** Navigation uses the declared page list in both desktop and mobile layouts. */
function AppNav({
  pathname,
  mobile = false,
  onSelect,
  collapsedGroups,
  onToggleGroup
}: {
  pathname: string
  mobile?: boolean
  onSelect: () => void
  /** The groups the reader has closed. A group absent from this list is open. */
  collapsedGroups: readonly string[]
  onToggleGroup: (group: string) => void
}): JSX.Element {
  const router = useRouter()
  const prefix = mobile ? 'mobile-' : ''
  // The declared page list and nothing else. There is no Home row, because
  // there is no home page — `/` redirects to the first app, so a Home row
  // would lead back to whichever row sits below it.
  const activePath = normalizePath(pathname)

  function navigate(path: string): void {
    onSelect()
    router.history.push(path)
  }

  // `flex-1` and `overflow-y-auto`: the page list is the only part of the rail
  // that scrolls, so the foot beside it stays in view.
  return (
    <nav
      aria-label="Internal apps"
      className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-3"
    >
      <p
        data-testid={`${prefix}app-title`}
        className="text-sidebar-foreground px-3 pt-2 pb-4 text-lg font-semibold"
      >
        BlytzPay Internal
      </p>
      {NAV_SECTIONS.map(section => {
        const slug = slugifyGroup(section.group)
        // Every section opens by default. A reader who has never touched the
        // rail should still see every destination it holds; collapsing is
        // something they choose, not something the rail starts them with.
        const open = !collapsedGroups.includes(section.group)
        // A closed group hides the row carrying `aria-current`, so the header
        // answers for it. Only while closed: an open group already shows that
        // row, and a second marker would name the same page twice.
        const holdsCurrent =
          !open &&
          section.pages.some(page => normalizePath(page.path) === activePath)

        return (
          <div key={section.group} className="flex flex-col gap-1">
            <button
              type="button"
              data-testid={`${prefix}nav-group-${slug}`}
              aria-expanded={open}
              aria-controls={`${prefix}nav-group-${slug}-items`}
              // A visually hidden span beside the dot is the obvious spelling,
              // but the accessible name trims each node and joins them without
              // a separator: "BlytzCollectholds the current page".
              aria-label={
                holdsCurrent
                  ? `${section.group}, holds the current page`
                  : undefined
              }
              className={cn(rowClass(holdsCurrent), 'justify-between')}
              onClick={() => onToggleGroup(section.group)}
            >
              <span className="flex items-center gap-2">
                {section.group}
                {holdsCurrent ? (
                  <span
                    aria-hidden="true"
                    className="bg-sidebar-accent-foreground size-1.5 shrink-0 rounded-full"
                  />
                ) : null}
              </span>
              <Icon
                name={open ? 'chevron-up' : 'chevron-down'}
                className="size-4 shrink-0"
              />
            </button>
            {open ? (
              <div
                id={`${prefix}nav-group-${slug}-items`}
                data-testid={`${prefix}nav-group-${slug}-items`}
                className="flex flex-col gap-1 pl-4"
              >
                {section.pages.map(page => (
                  <NavRow
                    key={page.id}
                    page={page}
                    active={activePath === normalizePath(page.path)}
                    testId={`${prefix}nav-${page.id}`}
                    onNavigate={() => navigate(page.path)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        )
      })}
    </nav>
  )
}

/**
 * The rail's pinned foot: who is signed in, sign out, then the theme toggle.
 *
 * Outside the scrolling page list, because that list grows with every app this
 * console gains and none of the three goes to an internal app. The address
 * sits beside the control that ends the session it names.
 *
 * @param props - Footer props.
 * @param props.mobile - True inside the sheet, false on the desktop rail.
 * @param props.onSelect - Closes the mobile sheet before the action runs.
 * @returns The rendered footer.
 */
function SidebarFooter({
  mobile = false,
  onSelect
}: {
  mobile?: boolean
  onSelect: () => void
}): JSX.Element {
  const { userName, onSignOut } = useSession()
  const prefix = mobile ? 'mobile-' : ''

  return (
    <div className="shrink-0 p-3 pt-0">
      <div className="bg-sidebar-border my-2 h-px" />
      <p
        data-testid={`${prefix}session-user`}
        className="text-sidebar-foreground/70 truncate px-3 py-2 text-sm"
      >
        {userName}
      </p>
      <button
        type="button"
        data-testid={`${prefix}session-sign-out`}
        className={cn(rowClass(false), 'w-full')}
        onClick={() => {
          onSelect()
          onSignOut()
        }}
      >
        <Icon name="log-out" className="size-4 shrink-0" />
        Sign out
      </button>
      <ThemeToggle
        data-testid={`${prefix}session-theme`}
        className={SIDEBAR_CONTROL_CLASS}
      />
    </div>
  )
}

/** The shell follows super's desktop rail and mobile navigation sheet. */
function RootLayout(): JSX.Element {
  const { userName, error } = useSession()
  const pathname = useRouterState({ select: state => state.location.pathname })
  const [menuOpen, setMenuOpen] = useState(false)
  // Held here, not in `AppNav`: the desktop rail and the mobile sheet are two
  // instances of it and must agree. Seeded in the initializer rather than in
  // an effect, which would render the rail open and then correct it.
  const [collapsedGroups, setCollapsedGroups] =
    useState<readonly string[]>(readCollapsedGroups)
  const menuTrigger = useRef<HTMLButtonElement>(null)
  const content = useRef<HTMLElement>(null)

  useEffect(() => {
    const close = (): void => setMenuOpen(false)
    window.addEventListener(IDLE_WARNING_EVENT, close)
    return () => window.removeEventListener(IDLE_WARNING_EVENT, close)
  }, [])

  function closeMenu(): void {
    setMenuOpen(false)
  }

  function toggleGroup(group: string): void {
    const next = collapsedGroups.includes(group)
      ? collapsedGroups.filter(name => name !== group)
      : [...collapsedGroups, group]

    setCollapsedGroups(next)
    // A browser that refuses the write still gets the rail; the choice simply
    // does not outlive the tab.
    writeCollapsedGroups(next)
  }

  return (
    <div
      data-testid="app-root"
      className="bg-background text-foreground min-h-svh md:grid md:grid-cols-rail"
    >
      <aside
        data-testid="console-sidebar"
        className="bg-sidebar hidden md:block"
      >
        {/* The aside stretches to the grid row, which is as tall as the report
            beside it. The foot pins to the viewport, not to that row, so the
            height and the stick belong on this inner box. */}
        <div className="sticky top-0 flex h-svh flex-col">
          <AppNav
            pathname={pathname}
            onSelect={closeMenu}
            collapsedGroups={collapsedGroups}
            onToggleGroup={toggleGroup}
          />
          <SidebarFooter onSelect={closeMenu} />
        </div>
      </aside>
      <div className="flex min-w-0 flex-col">
        <header className="bg-sidebar flex items-center gap-3 px-3 py-2 md:hidden">
          <Button
            data-testid="console-nav-toggle"
            ref={menuTrigger}
            variant="quiet"
            size="icon"
            aria-label="Menu"
            aria-expanded={menuOpen}
            className={SIDEBAR_CONTROL_CLASS}
            onClick={() => setMenuOpen(true)}
          >
            <Icon name="menu" />
          </Button>
          <span className="text-sidebar-foreground/70 truncate text-sm">
            {userName}
          </span>
        </header>
        <NavSheet
          data-testid="console-nav"
          open={menuOpen}
          onOpenChange={setMenuOpen}
          title="Internal navigation"
          // The sheet is a flex column with a gap of its own, which would show
          // as a seam above the pinned foot.
          className="gap-0"
          onCloseAutoFocus={event => {
            event.preventDefault()
            const target = menuTrigger.current?.getClientRects().length
              ? menuTrigger.current
              : content.current
            target?.focus()
          }}
        >
          <AppNav
            pathname={pathname}
            mobile
            onSelect={closeMenu}
            collapsedGroups={collapsedGroups}
            onToggleGroup={toggleGroup}
          />
          <SidebarFooter mobile onSelect={closeMenu} />
        </NavSheet>
        <main
          ref={content}
          tabIndex={-1}
          data-testid="console-content"
          className="flex min-w-0 flex-col gap-6 p-4 sm:p-6 md:p-8"
        >
          {error ? (
            <Notice tone="danger" data-testid="session-error">
              {error}
            </Notice>
          ) : null}
          <Outlet />
        </main>
        <Toaster />
      </div>
    </div>
  )
}
